import * as React from 'react';

import {
  BANDE,
  PAPIERS,
  LIBELLES_STRATEGIE,
  allureMontre,
  facteurGps,
  formaterChrono,
  formaterKm,
  type ParametresBracelet,
  type Passage,
} from './braceletEngine.ts';

export const POLICE_BRACELET = "'Barlow Condensed', 'Roboto Condensed', 'Arial Narrow', sans-serif";

type Props = {
  parametres: ParametresBracelet;
  passages: Passage[];   // passages de cette bande
  index: number;
  total: number;
  /** true : dimensions en mm et trait de decoupe (feuille a imprimer). */
  impression?: boolean;
};

/**
 * Une bande du bracelet, dessinee en millimetres (viewBox) : affichee a la
 * largeur du conteneur a l'ecran, a taille reelle a l'impression.
 */
export default function BraceletSvg({ parametres, passages, index, total, impression = false }: Props) {
  const C = PAPIERS[parametres.papier];
  const { longueur: L, hauteur: H, languette, entete, marge } = BANDE;
  const zone = L - languette - entete - marge * 2;
  const colonnes = Math.ceil(passages.length / 2);
  const largeur = Math.min(BANDE.caseMax, zone / colonnes);
  const hLigne = (H - marge * 2) / 2;
  const x0 = marge + entete;
  const xLanguette = L - languette;
  const avecHeures = parametres.tempsSec >= 3600;

  const titre = (parametres.nom || 'Course').toUpperCase();
  const objectif = formaterChrono(parametres.tempsSec, avecHeures);
  const tailleTitre = Math.min(3.4, (entete - 4) / (titre.length * 0.5));
  const tailleObjectif = Math.min(7.4, (entete - 4) / (objectif.length * 0.5));

  // Hachures de la languette (coupees au bord par le clipPath)
  const hachures: number[] = [];
  for (let k = -H; k < languette; k += 2.4) hachures.push(k);
  const idClip = `languette-${index}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${L} ${H}`}
      width={impression ? `${L}mm` : '100%'}
      height={impression ? `${H}mm` : undefined}
      role="img"
      aria-label={`Bracelet ${index + 1} sur ${total}`}
      style={{ display: 'block' }}
    >
      <defs>
        <clipPath id={idClip}>
          <rect x={xLanguette} y={0} width={languette} height={H} />
        </clipPath>
      </defs>
      <rect x={0} y={0} width={L} height={H} rx={impression ? 0 : 2} fill={C.fond} />

      {/* En-tete */}
      <rect x={marge} y={marge} width={entete - 1.5} height={H - marge * 2} rx={1} fill={C.accent} />
      <text x={marge + 2} y={marge + 5.2} fontFamily={POLICE_BRACELET} fontWeight={700} fontSize={tailleTitre} letterSpacing={0.15} fill={C.encreAccent}>
        {titre}
      </text>
      <text x={marge + 2} y={marge + 14.6} fontFamily={POLICE_BRACELET} fontWeight={800} fontSize={tailleObjectif} fill={C.encreAccent}>
        {objectif}
      </text>
      <text x={marge + 2} y={marge + 20.4} fontFamily={POLICE_BRACELET} fontWeight={600} fontSize={3.1} fill={C.encreAccent}>
        {formaterChrono(allureMontre(parametres.tempsSec / parametres.distanceKm, parametres))} /km {facteurGps(parametres) > 1 ? 'montre' : 'moy.'}
      </text>
      <text x={marge + 2} y={marge + 25.2} fontFamily={POLICE_BRACELET} fontWeight={500} fontSize={2.5} fill={C.encreAccent} opacity={0.85}>
        {formaterKm(parametres.distanceKm)} km · {LIBELLES_STRATEGIE[parametres.strategie]}
        {facteurGps(parametres) > 1 ? ` · GPS +${formaterKm((facteurGps(parametres) - 1) * 100, 2)} %` : ''}
      </text>
      {total > 1 && (
        <text x={marge + 2} y={H - marge - 1.6} fontFamily={POLICE_BRACELET} fontWeight={700} fontSize={2.4} fill={C.encreAccent}>
          BANDE {index + 1}/{total}
        </text>
      )}

      {/* Cases de passage : ligne 1 puis ligne 2 */}
      {passages.map((p, i) => {
        const ligne = i < colonnes ? 0 : 1;
        const col = ligne ? i - colonnes : i;
        const x = x0 + col * largeur;
        const y = marge + ligne * hLigne;
        const cx = x + largeur / 2;
        const groupeImpair = Math.floor((p.km - 1e-6) / 5) % 2 === 1;
        const temps = formaterChrono(p.temps);
        const tailleTemps = Math.min(5, (largeur - 1.6) / (temps.length * 0.56));
        const libelle = p.arrivee ? 'ARRIVÉE' : p.miCourse ? `½ ${formaterKm(p.km, 2)}` : `km ${formaterKm(p.km)}`;
        const couleurLibelle = p.repere && parametres.papier !== 'jaune' ? C.accent : C.encre;
        return (
          <g key={p.km}>
            {p.repere ? (
              <rect x={x + 0.25} y={y + 0.25} width={largeur - 0.5} height={hLigne - 0.5} rx={0.8} fill={C.doux} stroke={C.encre} strokeWidth={0.35} />
            ) : (
              groupeImpair && <rect x={x} y={y} width={largeur} height={hLigne} fill={C.doux} opacity={0.6} />
            )}
            <text x={cx} y={y + 3.3} textAnchor="middle" fontFamily={POLICE_BRACELET} fontWeight={p.repere ? 800 : 600} fontSize={2.5} fill={couleurLibelle}>
              {libelle}
            </text>
            <text
              x={cx}
              y={y + (parametres.afficherAllure ? 8.9 : 9.6)}
              textAnchor="middle"
              fontFamily={POLICE_BRACELET}
              fontWeight={p.repere ? 800 : 700}
              fontSize={tailleTemps}
              fill={C.encre}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {temps}
            </text>
            {parametres.afficherAllure && (
              <text x={cx} y={y + 12.6} textAnchor="middle" fontFamily={POLICE_BRACELET} fontWeight={500} fontSize={2.2} fill={C.encre} opacity={0.75}>
                {formaterChrono(p.allureTroncon)}/km
              </text>
            )}
          </g>
        );
      })}

      {/* Grille */}
      {Array.from({ length: Math.max(0, colonnes - 1) }, (_, c) => (
        <line key={c} x1={x0 + (c + 1) * largeur} y1={marge} x2={x0 + (c + 1) * largeur} y2={H - marge} stroke={C.trait} strokeWidth={0.15} />
      ))}
      <line x1={x0} y1={marge + hLigne} x2={x0 + colonnes * largeur} y2={marge + hLigne} stroke={C.trait} strokeWidth={0.2} />

      {/* Languette a coller */}
      <g clipPath={`url(#${idClip})`} opacity={0.55}>
        {hachures.map((k) => (
          <line key={k} x1={xLanguette + k} y1={H} x2={xLanguette + k + H} y2={0} stroke={C.trait} strokeWidth={0.2} />
        ))}
      </g>
      <line x1={xLanguette} y1={0} x2={xLanguette} y2={H} stroke={C.encre} strokeWidth={0.2} strokeDasharray="1 0.8" />
      <text
        transform={`translate(${xLanguette + languette / 2 + 1} ${H / 2}) rotate(-90)`}
        textAnchor="middle"
        fontFamily={POLICE_BRACELET}
        fontWeight={700}
        fontSize={2.6}
        letterSpacing={0.4}
        fill={C.encre}
        opacity={0.8}
      >
        COLLER
      </text>

      {impression && <rect x={0} y={0} width={L} height={H} fill="none" stroke="#777" strokeWidth={0.2} strokeDasharray="1.5 1" />}
    </svg>
  );
}
